import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null); // 'SUBSCRIPTION_EXPIRED' | 'SESSION_EXPIRED_ANOTHER_DEVICE' | null

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const detail = error.response?.data?.detail;
        const status = error.response?.status;
        
        if (detail === 'SESSION_EXPIRED_ANOTHER_DEVICE') {
          setSessionError('ANOTHER_DEVICE');
          logout();
        } else if (detail === 'SUBSCRIPTION_EXPIRED' || status === 402) {
          setSessionError('SUBSCRIPTION_EXPIRED');
          logout();
        } else if (status === 401 && token) {
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => { axios.interceptors.response.eject(interceptor); };
  }, [token]);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        await fetchUser();
      } else {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setSessionError(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'SUBSCRIPTION_EXPIRED') {
        setSessionError('SUBSCRIPTION_EXPIRED');
      }
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setSessionError(null);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const clearSessionError = () => setSessionError(null);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, sessionError, clearSessionError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
